// Ouvre la connexion au hub SignalR de profil dès qu'un token est disponible, la ferme au logout.
import type {
  BigFive,
  DimensionThemeProgress,
  LolaPushedMessage,
  MbtiResult,
  PersonalityHighlights,
  ProfileShareable,
  ProfileSummary,
  QualificationSnapshot,
  RememberedItem,
} from '~/types/NotificationsHub'
import type { SignalRHubOptions } from '~/utils/SignalRHubUtils'
import { watch } from 'vue'
import { useAuth } from '~/composables/useAuth'
import { useAgentInformationsStore } from '~/stores/agentInformationsStore'
import { useAnalysisStore } from '~/stores/analysisStore'
import { useChatStore } from '~/stores/chatStore'
import { useMbtiStore } from '~/stores/mbtiStore'
import { useUserStore } from '~/stores/user'
import { normalizeBaseUrl } from '~/utils/helpers'
import { SignalRHubUtils } from '~/utils/SignalRHubUtils'

export default defineNuxtPlugin((): void => {
  const config = useRuntimeConfig()
  const { getApiToken } = useAuth()
  const userStore = useUserStore()
  const agentInformations = useAgentInformationsStore()
  const analysisStore = useAnalysisStore()
  const mbtiStore = useMbtiStore()
  const chatStore = useChatStore()

  const hub: SignalRHubUtils = new SignalRHubUtils()
  const options: SignalRHubOptions = {
    hubUrl: `${normalizeBaseUrl(config.public.frontApiBaseUrl as string)}/hubs/notifications`,
    accessTokenFactory: async (): Promise<string> => (await getApiToken()) ?? '',
    // Le payload arrive en `unknown` ; chaque handler le caste vers son type DTO (pas de validation runtime).
    handlers: {
      SummaryChanged: (payload: unknown): void =>
        agentInformations.setSummary(payload as ProfileSummary),
      QualificationChanged: (payload: unknown): void =>
        agentInformations.applyQualificationFromLola(payload as QualificationSnapshot),
      DimensionProgressChanged: (payload: unknown): void =>
        agentInformations.applyDimensions(payload as DimensionThemeProgress[]),
      RememberedItemsChanged: (payload: unknown): void =>
        agentInformations.setRemembered(payload as RememberedItem[]),
      BigFiveChanged: (payload: unknown): void => analysisStore.setBigFive(payload as BigFive),
      PersonalityHighlightsChanged: (payload: unknown): void =>
        analysisStore.setPersonalityHighlights(payload as PersonalityHighlights),
      MbtiResultChanged: (payload: unknown): void =>
        mbtiStore.applyMbtiResult(payload as MbtiResult),
      ProfileShareableChanged: (payload: unknown): void =>
        userStore.setShareable(payload as ProfileShareable),
      LolaMessageReady: (payload: unknown): void => {
        chatStore.applyPushedMessage(payload as LolaPushedMessage)
      },
    },
  }

  watch(
    (): boolean => userStore.isLoggedIn,
    (isLoggedIn: boolean): void => {
      if (isLoggedIn) void hub.start(options)
      else void hub.stop()
    },
    { immediate: true },
  )
})
